import { analytics } from '@/features/analytics/events'
import { isCaptchaFailedError, useTurnstileCaptcha } from '@/features/turnstile'
import { t } from '@lingui/core/macro'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { useSubmitNectarTask } from '../api'
import { useAudioRecorder } from './useAudioRecorder'

// Server signals a lapsed assignment with a dedicated, stable error code (status 409).
const isAssignmentExpiredError = (err: unknown): boolean =>
  isAxiosError(err) &&
  (err.response?.data as { error?: { code?: string } })?.error?.code === 'ASSIGNMENT_EXPIRED'

type Recorder = ReturnType<typeof useAudioRecorder>
type Captcha = ReturnType<typeof useTurnstileCaptcha>

type UseSubmitAudioRecordingOptions = {
  recorder: Pick<Recorder, 'markSubmitting' | 'markSubmitted' | 'markRecorded' | 'getClientMetadata'>
  captcha: Pick<Captcha, 'token' | 'reset'>
}

type SubmitArgs = {
  projectId: string
  taskId: string
  taskIndex: number
  blob: Blob
  seconds: number
  onSuccess?: () => void
}

export const useSubmitAudioRecording = ({ recorder, captcha }: UseSubmitAudioRecordingOptions) => {
  const { mutateAsync: submitTask } = useSubmitNectarTask()
  const queryClient = useQueryClient()

  return async ({ projectId, taskId, taskIndex, blob, seconds, onSuccess }: SubmitArgs) => {
    recorder.markSubmitting()
    try {
      const file = new File([blob], 'recording.wav', { type: 'audio/wav' })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('duration', String(seconds))
      const meta = recorder.getClientMetadata()
      if (meta) formData.append('clientMetadata', JSON.stringify(meta))

      await submitTask({
        projectId,
        taskId,
        data: formData,
        turnstileToken: captcha.token ?? undefined
      })
      captcha.reset()
      toast.success(t`Submitted!`)
      analytics.missionTaskSubmitted(projectId, taskId, taskIndex)
      recorder.markSubmitted()
      onSuccess?.()
    } catch (err) {
      captcha.reset()
      if (isCaptchaFailedError(err)) {
        toast.error(t`Verification failed. Please try again.`)
        recorder.markRecorded()
      } else if (isAssignmentExpiredError(err)) {
        toast.error(t`Your time for this task ran out. Loading a new task…`)
        void queryClient.invalidateQueries({ queryKey: ['NECTAR_NEXT_TASK', projectId] })
      } else {
        toast.error(t`Failed to submit recording`)
        recorder.markRecorded()
      }
    }
  }
}
